/* Altyapı & Abonelikler — host/proxy/domain ödeme takibi (admin) */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Server, Globe, ShieldCheck, Box,
} from "lucide-react";
import { http, errMsg } from "@/lib/api";
import "@/pages/Admin.css";

const infraApi = {
  list: () => http.get("/admin/infra").then((r) => r.data),
  create: (b) => http.post("/admin/infra", b).then((r) => r.data),
  update: ({ id, ...b }) => http.put(`/admin/infra/${id}`, b).then((r) => r.data),
  remove: (id) => http.delete(`/admin/infra/${id}`).then((r) => r.data),
  markPaid: (id) => http.post(`/admin/infra/${id}/mark-paid`).then((r) => r.data),
};

const CAT = {
  host: { label: "Host", Icon: Server, color: "#0A84FF" },
  proxy: { label: "Proxy", Icon: ShieldCheck, color: "#2bd98a" },
  domain: { label: "Domain", Icon: Globe, color: "#f0b90b" },
  other: { label: "Diğer", Icon: Box, color: "#a1a1aa" },
};
const CUR = { USD: "$", EUR: "€", TRY: "₺" };
const fmtDate = (d) => (d ? d.split("T")[0] : "—");

const EMPTY = {
  name: "", category: "other", provider: "", url: "", detail: "",
  cost: "", currency: "USD", period: "monthly",
  first_payment: "", last_payment: "", next_payment: "",
  status: "active", notes: "",
};

function InfraForm({ initial, onSave, onCancel, busy }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const inp = "w-full h-8 px-2 rounded-sm bg-[#0F0F0F] border border-[#27272A] text-xs text-zinc-200 font-mono focus:border-[#ff5d15]/50 outline-none";
  const lbl = "block text-[9px] uppercase tracking-wider text-zinc-500 mb-1";
  const clean = () => {
    const out = { ...f, cost: f.cost === "" ? 0 : Number(f.cost) };
    ["first_payment", "last_payment", "next_payment"].forEach((k) => {
      if (!out[k]) out[k] = null;
    });
    return out;
  };
  return (
    <div className="p-4 bg-[#0F0F0F] border-t border-dashed border-[#27272A]"
         data-testid="infra-form">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><span className={lbl}>Servis Adı *</span>
          <input className={inp} value={f.name} onChange={set("name")}
                 placeholder="örn. Hetzner VPS" data-testid="infra-form-name" /></div>
        <div><span className={lbl}>Kategori</span>
          <select className={inp} value={f.category} onChange={set("category")}>
            <option value="host">Host</option><option value="proxy">Proxy</option>
            <option value="domain">Domain</option><option value="other">Diğer</option>
          </select></div>
        <div><span className={lbl}>Sağlayıcı</span>
          <input className={inp} value={f.provider} onChange={set("provider")} /></div>
        <div><span className={lbl}>Panel URL</span>
          <input className={inp} value={f.url} onChange={set("url")}
                 placeholder="https://…" /></div>
        <div className="col-span-2"><span className={lbl}>Detay (IP / plan)</span>
          <input className={inp} value={f.detail} onChange={set("detail")} /></div>
        <div><span className={lbl}>Tutar</span>
          <input className={inp} inputMode="decimal" value={f.cost}
                 onChange={set("cost")} data-testid="infra-form-cost" /></div>
        <div className="flex gap-2">
          <div className="flex-1"><span className={lbl}>Para</span>
            <select className={inp} value={f.currency} onChange={set("currency")}>
              <option>USD</option><option>EUR</option><option>TRY</option>
            </select></div>
          <div className="flex-1"><span className={lbl}>Periyot</span>
            <select className={inp} value={f.period} onChange={set("period")}>
              <option value="monthly">Aylık</option>
              <option value="yearly">Yıllık</option>
            </select></div>
        </div>
        <div><span className={lbl}>İlk Ödeme</span>
          <input type="date" className={inp} value={f.first_payment || ""}
                 onChange={set("first_payment")} data-testid="infra-form-first" /></div>
        <div><span className={lbl}>Son Ödeme</span>
          <input type="date" className={inp} value={f.last_payment || ""}
                 onChange={set("last_payment")} /></div>
        <div><span className={lbl}>Sonraki Ödeme</span>
          <input type="date" className={inp} value={f.next_payment || ""}
                 onChange={set("next_payment")} data-testid="infra-form-next" /></div>
        <div><span className={lbl}>Durum</span>
          <select className={inp} value={f.status} onChange={set("status")}>
            <option value="active">Aktif</option>
            <option value="cancelled">İptal edildi</option>
          </select></div>
        <div className="col-span-2 md:col-span-4"><span className={lbl}>Not</span>
          <input className={inp} value={f.notes} onChange={set("notes")} /></div>
      </div>
      <div className="flex gap-2 mt-3">
        <button type="button" disabled={!f.name.trim() || busy}
                onClick={() => onSave(clean())}
                data-testid="infra-form-save"
                className="btn btn-orange">
          {busy ? "Kaydediliyor…" : "Kaydet"}
        </button>
        <button type="button" onClick={onCancel}
                className="btn btn-ghost">
          Vazgeç
        </button>
      </div>
    </div>
  );
}

export default function InfraServicesCard() {
  const qc = useQueryClient();
  const [editId, setEditId] = useState(null); // "new" | service id | null
  const { data: services } = useQuery({
    queryKey: ["admin-infra"], queryFn: infraApi.list, refetchInterval: 60000,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin-infra"] });
  const err = (e) => toast.error(errMsg(e));

  const createMut = useMutation({
    mutationFn: infraApi.create,
    onSuccess: () => { toast.success("Servis eklendi"); setEditId(null); invalidate(); },
    onError: err,
  });
  const updateMut = useMutation({
    mutationFn: infraApi.update,
    onSuccess: () => { toast.success("Servis güncellendi"); setEditId(null); invalidate(); },
    onError: err,
  });
  const removeMut = useMutation({
    mutationFn: infraApi.remove,
    onSuccess: () => { toast.success("Servis silindi"); invalidate(); },
    onError: err,
  });
  const paidMut = useMutation({
    mutationFn: infraApi.markPaid,
    onSuccess: (d) => {
      toast.success(`Ödeme kaydedildi · sonraki vade ${fmtDate(d.next_payment)}`);
      invalidate();
    },
    onError: err,
  });

  return (
    <section className="card" data-testid="infra-services-card">
      <span className="cb3" /><span className="cb4" />
      <div className="ch-head">
        <div className="ch-ic">
          <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="6" rx="2" /><rect x="3" y="14" width="18" height="6" rx="2" /><path d="M7 7h.01M7 17h.01" /></svg>
        </div>
        <div className="ch-tt">
          <h2>
            Altyapı &amp; Abonelikler <span className="n mono">({services?.length || 0})</span>
          </h2>
          <div className="sub">Vade ≤3 gün → <b>Telegram uyarısı</b></div>
        </div>
        <div className="ch-act">
          <button
            type="button"
            className="btn btn-orange"
            data-testid="infra-add-btn"
            onClick={() => setEditId(editId === "new" ? null : "new")}
          >
            <svg viewBox="0 0 24 24" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
            Servis Ekle
          </button>
        </div>
      </div>

      {editId === "new" && (
        <InfraForm initial={{}} busy={createMut.isPending}
                   onSave={(b) => createMut.mutate(b)}
                   onCancel={() => setEditId(null)} />
      )}

      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>Servis</th>
              <th>Detay</th>
              <th>Tutar</th>
              <th>İlk Ödeme</th>
              <th>Son Ödeme</th>
              <th>Sonraki Vade</th>
              <th className="r">Aksiyon</th>
            </tr>
          </thead>
          <tbody>
            {services?.map((s) => {
              const cat = CAT[s.category] || CAT.other;
              return (
                <Row key={s.id} s={s} cat={cat}
                     editing={editId === s.id}
                     onEdit={() => setEditId(editId === s.id ? null : s.id)}
                     onSave={(b) => updateMut.mutate({ id: s.id, ...b })}
                     onCancel={() => setEditId(null)}
                     busy={updateMut.isPending}
                     onPaid={() => paidMut.mutate(s.id)}
                     paidBusy={paidMut.isPending}
                     onDelete={() => {
                       if (window.confirm(`"${s.name}" silinsin mi?`))
                         removeMut.mutate(s.id);
                     }} />
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Row({ s, cat, editing, onEdit, onSave, onCancel, busy, onPaid, paidBusy, onDelete }) {
  // svc-ic kategoriye göre renk (blue/grn/gold/grey)
  const icCls =
    s.category === "host" ? "blue"
    : s.category === "proxy" ? "grn"
    : s.category === "domain" ? "gold"
    : "";
  // Sonraki vade pill rengi (grn/org/gold/mut)
  const stateMap = {
    overdue: { cls: "down", txt: `${Math.abs(s.days_left)} gün GECİKTİ` },
    soon: { cls: "org", txt: s.days_left === 0 ? "BUGÜN" : `${s.days_left} gün kaldı` },
    ok: { cls: "grn", txt: `${s.days_left} gün kaldı` },
    cancelled: { cls: "mut", txt: "İPTAL" },
    unknown: { cls: "tagin", txt: "tarih gir" },
  };
  const st = stateMap[s.payment_state] || stateMap.unknown;

  return (
    <>
      <tr data-testid={`infra-row-${s.id}`}>
        <td>
          <div className="svc">
            <div className={`svc-ic ${icCls}`}>
              <svg viewBox="0 0 24 24">
                {s.category === "host" && (<><rect x="3" y="4" width="18" height="7" rx="2" /><rect x="3" y="13" width="18" height="7" rx="2" /><path d="M7 7.5h.01M7 16.5h.01" /></>)}
                {s.category === "proxy" && (<><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></>)}
                {s.category === "domain" && (<><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></>)}
                {(s.category !== "host" && s.category !== "proxy" && s.category !== "domain") && (<rect x="4" y="4" width="16" height="16" rx="2" />)}
              </svg>
            </div>
            <div>
              <div className="svc-n">
                <span className={s.status === "cancelled" ? "line-through" : ""}>{s.name}</span>
                {s.url && (
                  <a className="lk" href={s.url} target="_blank" rel="noreferrer" aria-label="aç">
                    <svg viewBox="0 0 24 24"><path d="M14 4h6v6M20 4l-9 9M9 5H5a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h13a1 1 0 0 0 1-1v-4" /></svg>
                  </a>
                )}
              </div>
              <div className="svc-s">{cat.label}{s.provider ? ` · ${s.provider}` : ""}</div>
            </div>
          </div>
        </td>
        <td>
          <span className="det">
            {s.detail || "—"}
            {s.notes && <span className="hint">{s.notes}</span>}
          </span>
        </td>
        <td>
          {s.cost ? (
            <span className="amt">
              {CUR[s.currency] || "$"}{Number(s.cost).toFixed(2)}
              <small>/{s.period === "yearly" ? "yıl" : "ay"}</small>
            </span>
          ) : (
            <span className="dash">—</span>
          )}
        </td>
        <td>{s.first_payment ? <span className="dt">{fmtDate(s.first_payment)}</span> : <span className="dash">—</span>}</td>
        <td>{s.last_payment ? <span className="dt">{fmtDate(s.last_payment)}</span> : <span className="dash">—</span>}</td>
        <td>
          <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
            {s.next_payment ? <span className="dt" style={{ color: "var(--txt)" }}>{fmtDate(s.next_payment)}</span> : <span className="dash">—</span>}
            {st.cls === "tagin" ? (
              <span className="tag-in" data-testid={`infra-state-${s.id}`}>
                <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" strokeLinecap="round" /></svg>
                {st.txt}
              </span>
            ) : (
              <span className={`pill ${st.cls}`} data-testid={`infra-state-${s.id}`}>
                <span className="pd" />{st.txt}
              </span>
            )}
          </div>
        </td>
        <td className="r">
          <div className="row-act">
            {s.status === "active" && (
              <button
                type="button"
                className="ico paid"
                onClick={onPaid}
                disabled={paidBusy}
                data-testid={`infra-paid-${s.id}`}
              >
                <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M8.5 12l2.5 2.5 4.5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                Ödendi
              </button>
            )}
            <button
              type="button"
              className="ico edit"
              onClick={onEdit}
              data-testid={`infra-edit-${s.id}`}
              aria-label="düzenle"
            >
              <svg viewBox="0 0 24 24"><path d="M4 20h4l10-10-4-4L4 16v4z" /><path d="M13.5 6.5l4 4" /></svg>
            </button>
            <button
              type="button"
              className="ico del"
              onClick={onDelete}
              data-testid={`infra-delete-${s.id}`}
              aria-label="sil"
            >
              <svg viewBox="0 0 24 24"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" /></svg>
            </button>
          </div>
        </td>
      </tr>
      {editing && (
        <tr>
          <td colSpan={7} style={{ padding: 0 }}>
            <InfraForm initial={s} busy={busy} onSave={onSave} onCancel={onCancel} />
          </td>
        </tr>
      )}
    </>
  );
}
